import { useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Divider,
  EmptyState,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@jem-hr/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  Eye,
  Info,
  RefreshCw,
  UserMinus,
  UserPlus,
  Users,
  Workflow,
} from "lucide-react";

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type DataState = "ready" | "empty" | "loading" | "error";
type HealthBucket = "healthy" | "degraded" | "down";
type Period = "today" | "7d" | "30d" | "custom";
type ChannelKey = "scheduled-sync" | "workflow-events";

interface ChannelMetric {
  attempted: number;
  successful: number;
}

interface SyncCategoryBreakdown {
  key: string;
  label: string;
  attempted: number;
  successful: number;
}

interface FailedRecord {
  id: string;
  detail: string;
  employeeName: string;
  reason: string;
  timestamp: string;
}

interface ChannelDetail {
  key: ChannelKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  metric: ChannelMetric;
  breakdown: SyncCategoryBreakdown[];
  breakdownLabel: string;
  failedRecords: FailedRecord[];
  // Per-channel net change in blocked-employee population during the period.
  // Sum across channels equals OVERALL_BLOCKED_NEW / OVERALL_UNBLOCKED.
  newBlocks: number;
  unblocked: number;
}

interface SyncRunFailure {
  detail: string;
  employeeName: string;
  reason: string;
}

type SyncRunStatus = "in-progress" | "success" | "partial-success" | "failure";

interface SyncRun {
  id: string;
  timestamp: string;
  channel: "scheduled" | "adhoc";
  status: SyncRunStatus;
  succeeded: { created: number; updated: number; total: number };
  failed: { created: number; updated: number; total: number };
  blocked: number;
  unblocked: number;
  failedRecords: SyncRunFailure[];
}

const SYNC_STATUS_TAG: Record<
  SyncRunStatus,
  { label: string; variant: "success" | "drafted" | "failed" | "processing" }
> = {
  "in-progress": { label: "In progress", variant: "processing" },
  success: { label: "Success", variant: "success" },
  "partial-success": { label: "Partial success", variant: "drafted" },
  failure: { label: "Failure", variant: "failed" },
};

interface EmployeeEvent {
  type: "blocked" | "unblocked" | "sync-failed" | "sync-succeeded";
  description: string;
  timestamp: string;
}

interface EmployeeProfile {
  id: string;
  name: string;
  employeeNumber: string;
  site: string;
  status: "blocked" | "unblocked" | "ok";
  reason?: string;
  blockedSince?: string;
  events: EmployeeEvent[];
}

interface ClientOption {
  value: string;
  label: string;
}

/* ================================================================== */
/*  Mock data                                                          */
/* ================================================================== */

const CLIENT_OPTIONS: ClientOption[] = [
  { value: "acme", label: "Acme Corporation" },
  { value: "servest", label: "Servest" },
  { value: "tsebo", label: "Tsebo Holdings" },
];

/* Mocks shaped to the candidate API response:
 * { all, batch, adhoc } each with succeeded { total, created, updated },
 * failed { total, created, updated }, blocked, unblocked.
 * "Other" = total - created - updated (e.g. deactivations / other op types
 * not broken out at this level).
 */

// `all` bucket — full payload surfaced in the Overall hero
const OVERALL: ChannelMetric = { attempted: 150, successful: 120 }; // 120 + 30 failed
const OVERALL_SUCCEEDED_CREATED = 40;
const OVERALL_SUCCEEDED_UPDATED = 70;
const OVERALL_FAILED_CREATED = 8;
const OVERALL_FAILED_UPDATED = 18;

const CHANNELS: ChannelDetail[] = [
  {
    key: "scheduled-sync",
    label: "Scheduled Updates",
    description: "Bulk employee changes pushed on a schedule (e.g. nightly batch sync).",
    icon: <Calendar size={16} />,
    // batch: succeeded.total 85, failed.total 15 → attempted 100
    metric: { attempted: 100, successful: 85 },
    breakdownLabel: "By change type",
    breakdown: [
      // batch.created: 30 succeeded, 5 failed
      { key: "created", label: "Created", attempted: 35, successful: 30 },
      // batch.updated: 50 succeeded, 8 failed
      { key: "updated", label: "Updated", attempted: 58, successful: 50 },
      // batch.other = 85 - 30 - 50 = 5 succeeded, 15 - 5 - 8 = 2 failed
      { key: "other", label: "Other", attempted: 7, successful: 5 },
    ],
    failedRecords: [
      {
        id: "ss-1",
        detail: "Update — banking details",
        employeeName: "Naledi Khumalo",
        reason: "Validation failed: branch code not recognised",
        timestamp: "29 Apr 2026, 09:42",
      },
      {
        id: "ss-2",
        detail: "Update — tax number",
        employeeName: "Sipho Dlamini",
        reason: "Downstream timeout after 30s",
        timestamp: "29 Apr 2026, 09:18",
      },
      {
        id: "ss-3",
        detail: "Update — address",
        employeeName: "Thandi Mokoena",
        reason: "Validation failed: postal code missing",
        timestamp: "29 Apr 2026, 08:55",
      },
      {
        id: "ss-4",
        detail: "Create — new employee",
        employeeName: "Brendan Naidoo",
        reason: "Schema mismatch on phoneNumber field",
        timestamp: "29 Apr 2026, 08:30",
      },
    ],
    newBlocks: 2, // batch.blocked
    unblocked: 5, // batch.unblocked
  },
  {
    key: "workflow-events",
    label: "Adhoc Updates",
    description: "Real-time changes pushed when a user updates an employee.",
    icon: <Workflow size={16} />,
    // adhoc: succeeded.total 35, failed.total 15 → attempted 50
    metric: { attempted: 50, successful: 35 },
    breakdownLabel: "By change type",
    breakdown: [
      // adhoc.created: 10 succeeded, 3 failed
      { key: "created", label: "Created", attempted: 13, successful: 10 },
      // adhoc.updated: 20 succeeded, 10 failed
      { key: "updated", label: "Updated", attempted: 30, successful: 20 },
      // adhoc.other = 35 - 10 - 20 = 5 succeeded, 15 - 3 - 10 = 2 failed
      { key: "other", label: "Other", attempted: 7, successful: 5 },
    ],
    failedRecords: [
      {
        id: "we-1",
        detail: "Leave approval webhook",
        employeeName: "Lerato Mahlangu",
        reason: "Downstream rejected payload — missing manager ID",
        timestamp: "29 Apr 2026, 08:12",
      },
    ],
    newBlocks: 2, // adhoc.blocked
    unblocked: 5, // adhoc.unblocked
  },
];

// Period totals — derived from all.blocked / all.unblocked
const NEW_BLOCKS = 4;
const NEW_UNBLOCKS = 10;

// Per-sync log — drives the new sync-runs table.
const SYNC_RUNS: SyncRun[] = [
  {
    id: "sr-0",
    timestamp: "30 Apr 2026, 14:02",
    channel: "scheduled",
    status: "in-progress",
    succeeded: { created: 4, updated: 42, total: 46 },
    failed: { created: 1, updated: 4, total: 5 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [
      { detail: "Create — new employee", employeeName: "Carla Botha", reason: "Schema mismatch on phoneNumber field" },
      { detail: "Update — banking details", employeeName: "Tshepo Mahlangu", reason: "Validation failed: branch code not recognised" },
      { detail: "Update — tax number", employeeName: "Aisha Ismail", reason: "Downstream timeout after 30s" },
      { detail: "Update — address", employeeName: "Lethabo Sebola", reason: "Validation failed: postal code missing" },
      { detail: "Update — emergency contact", employeeName: "Daniel Stevens", reason: "Phone number invalid format" },
    ],
  },
  {
    id: "sr-1a",
    timestamp: "30 Apr 2026, 13:30",
    channel: "adhoc",
    status: "success",
    succeeded: { created: 0, updated: 1, total: 1 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [],
  },
  {
    id: "sr-1b",
    timestamp: "30 Apr 2026, 12:15",
    channel: "adhoc",
    status: "partial-success",
    succeeded: { created: 0, updated: 2, total: 2 },
    failed: { created: 0, updated: 1, total: 1 },
    blocked: 0,
    unblocked: 1,
    failedRecords: [
      { detail: "Update — banking details", employeeName: "Megan Williams", reason: "Validation failed: account holder name mismatch" },
    ],
  },
  {
    id: "sr-1c",
    timestamp: "30 Apr 2026, 11:00",
    channel: "adhoc",
    status: "failure",
    succeeded: { created: 0, updated: 0, total: 0 },
    failed: { created: 0, updated: 1, total: 1 },
    blocked: 1,
    unblocked: 0,
    failedRecords: [
      { detail: "Leave approval webhook", employeeName: "Kwame Asante", reason: "Downstream rejected payload — missing manager ID" },
    ],
  },
  {
    id: "sr-1d",
    timestamp: "30 Apr 2026, 10:30",
    channel: "scheduled",
    status: "success",
    succeeded: { created: 2, updated: 18, total: 20 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [],
  },
  {
    id: "sr-1e",
    timestamp: "30 Apr 2026, 09:45",
    channel: "adhoc",
    status: "success",
    succeeded: { created: 0, updated: 1, total: 1 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [],
  },
  {
    id: "sr-1f",
    timestamp: "30 Apr 2026, 08:20",
    channel: "adhoc",
    status: "partial-success",
    succeeded: { created: 0, updated: 1, total: 1 },
    failed: { created: 0, updated: 2, total: 2 },
    blocked: 1,
    unblocked: 0,
    failedRecords: [
      { detail: "Update — surname", employeeName: "Bongiwe Ndlovu", reason: "Length exceeds 50 character limit" },
      { detail: "Update — name", employeeName: "Sibongile Mokoena", reason: "Special character not allowed in first name" },
    ],
  },
  {
    id: "sr-1",
    timestamp: "30 Apr 2026, 06:00",
    channel: "scheduled",
    status: "partial-success",
    succeeded: { created: 18, updated: 162, total: 184 },
    failed: { created: 12, updated: 18, total: 30 },
    blocked: 6,
    unblocked: 4,
    failedRecords: [
      { detail: "Update — banking details", employeeName: "Naledi Khumalo", reason: "Validation failed: branch code not recognised" },
      { detail: "Update — tax number", employeeName: "Sipho Dlamini", reason: "Downstream timeout after 30s" },
      { detail: "Update — address", employeeName: "Thandi Mokoena", reason: "Validation failed: postal code missing" },
      { detail: "Create — new employee", employeeName: "Themba Sithole", reason: "Missing required field: ID type" },
      { detail: "Create — new employee", employeeName: "Zanele Mthembu", reason: "Schema mismatch on phoneNumber field" },
      { detail: "Create — new employee", employeeName: "Pieter de Klerk", reason: "Duplicate ID number in destination system" },
      { detail: "Create — new employee", employeeName: "Khanyi Maseko", reason: "Validation failed: surname contains special character" },
      { detail: "Create — new employee", employeeName: "Tumi Phatudi", reason: "Missing required field: site code" },
      { detail: "Create — new employee", employeeName: "Ahmed Patel", reason: "Downstream timeout after 30s" },
      { detail: "Create — new employee", employeeName: "Fatima Adams", reason: "Schema mismatch on phoneNumber field" },
      { detail: "Create — new employee", employeeName: "Mandla Zulu", reason: "Missing required field: ID type" },
      { detail: "Create — new employee", employeeName: "Anele van der Merwe", reason: "Duplicate ID number in destination system" },
      { detail: "Create — new employee", employeeName: "Brendan Naidoo", reason: "Schema mismatch on phoneNumber field" },
      { detail: "Create — new employee", employeeName: "Lindiwe Khoza", reason: "Missing required field: ID type" },
      { detail: "Create — new employee", employeeName: "Bongiwe Ndlovu", reason: "Validation failed: surname contains special character" },
      { detail: "Update — banking details", employeeName: "Refiloe Letsoalo", reason: "Validation failed: branch code not recognised" },
      { detail: "Update — banking details", employeeName: "Ryan O'Connor", reason: "Validation failed: account holder name mismatch" },
      { detail: "Update — tax number", employeeName: "Nokuthula Tshabalala", reason: "Downstream timeout after 30s" },
      { detail: "Update — address", employeeName: "Johan Pretorius", reason: "Validation failed: postal code missing" },
      { detail: "Update — address", employeeName: "Zinhle Khumalo", reason: "Validation failed: postal code missing" },
      { detail: "Update — emergency contact", employeeName: "Tshepo Mahlangu", reason: "Phone number invalid format" },
      { detail: "Update — email", employeeName: "Kwame Asante", reason: "Domain not whitelisted by destination system" },
      { detail: "Update — banking details", employeeName: "Megan Williams", reason: "Validation failed: branch code not recognised" },
      { detail: "Update — surname", employeeName: "Sibongile Mokoena", reason: "Length exceeds 50 character limit" },
      { detail: "Update — name", employeeName: "Daniel Stevens", reason: "Special character not allowed in first name" },
      { detail: "Update — banking details", employeeName: "Aisha Ismail", reason: "Validation failed: account holder name mismatch" },
      { detail: "Update — tax number", employeeName: "Lethabo Sebola", reason: "Validation failed: SARS reference invalid" },
      { detail: "Update — address", employeeName: "Carla Botha", reason: "Validation failed: postal code missing" },
      { detail: "Update — emergency contact", employeeName: "Mandisa Zulu", reason: "Phone number invalid format" },
      { detail: "Update — bank account", employeeName: "Joseph Pillay", reason: "Downstream timeout after 30s" },
    ],
  },
  {
    id: "sr-2",
    timestamp: "29 Apr 2026, 18:42",
    channel: "adhoc",
    status: "success",
    succeeded: { created: 1, updated: 0, total: 1 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 1,
    failedRecords: [],
  },
  {
    id: "sr-3",
    timestamp: "29 Apr 2026, 14:32",
    channel: "adhoc",
    status: "failure",
    succeeded: { created: 0, updated: 0, total: 0 },
    failed: { created: 0, updated: 1, total: 1 },
    blocked: 1,
    unblocked: 0,
    failedRecords: [
      {
        detail: "Leave approval webhook",
        employeeName: "Lerato Mahlangu",
        reason: "Downstream rejected payload — missing manager ID",
      },
    ],
  },
  {
    id: "sr-4",
    timestamp: "29 Apr 2026, 06:00",
    channel: "scheduled",
    status: "partial-success",
    succeeded: { created: 12, updated: 156, total: 168 },
    failed: { created: 10, updated: 15, total: 25 },
    blocked: 2,
    unblocked: 5,
    failedRecords: [
      { detail: "Create — new employee", employeeName: "Brendan Naidoo", reason: "Schema mismatch on phoneNumber field" },
      { detail: "Create — new employee", employeeName: "Anele van der Merwe", reason: "Duplicate ID number in destination system" },
      { detail: "Create — new employee", employeeName: "Themba Sithole", reason: "Missing required field: ID type" },
      { detail: "Create — new employee", employeeName: "Zanele Mthembu", reason: "Schema mismatch on phoneNumber field" },
      { detail: "Create — new employee", employeeName: "Pieter de Klerk", reason: "Duplicate ID number in destination system" },
      { detail: "Create — new employee", employeeName: "Khanyi Maseko", reason: "Validation failed: surname contains special character" },
      { detail: "Create — new employee", employeeName: "Tumi Phatudi", reason: "Missing required field: site code" },
      { detail: "Create — new employee", employeeName: "Ahmed Patel", reason: "Downstream timeout after 30s" },
      { detail: "Create — new employee", employeeName: "Fatima Adams", reason: "Schema mismatch on phoneNumber field" },
      { detail: "Create — new employee", employeeName: "Mandla Zulu", reason: "Missing required field: ID type" },
      { detail: "Update — banking details", employeeName: "Refiloe Letsoalo", reason: "Validation failed: branch code not recognised" },
      { detail: "Update — banking details", employeeName: "Ryan O'Connor", reason: "Validation failed: account holder name mismatch" },
      { detail: "Update — tax number", employeeName: "Nokuthula Tshabalala", reason: "Downstream timeout after 30s" },
      { detail: "Update — address", employeeName: "Johan Pretorius", reason: "Validation failed: postal code missing" },
      { detail: "Update — address", employeeName: "Zinhle Khumalo", reason: "Validation failed: postal code missing" },
      { detail: "Update — emergency contact", employeeName: "Tshepo Mahlangu", reason: "Phone number invalid format" },
      { detail: "Update — email", employeeName: "Kwame Asante", reason: "Domain not whitelisted by destination system" },
      { detail: "Update — banking details", employeeName: "Megan Williams", reason: "Validation failed: branch code not recognised" },
      { detail: "Update — surname", employeeName: "Bongiwe Ndlovu", reason: "Length exceeds 50 character limit" },
      { detail: "Update — name", employeeName: "Sibongile Mokoena", reason: "Special character not allowed in first name" },
      { detail: "Update — banking details", employeeName: "Daniel Stevens", reason: "Validation failed: account holder name mismatch" },
      { detail: "Update — tax number", employeeName: "Aisha Ismail", reason: "Validation failed: SARS reference invalid" },
      { detail: "Update — address", employeeName: "Lethabo Sebola", reason: "Validation failed: postal code missing" },
      { detail: "Update — emergency contact", employeeName: "Carla Botha", reason: "Phone number invalid format" },
      { detail: "Update — bank account", employeeName: "Lindiwe Khoza", reason: "Downstream timeout after 30s" },
    ],
  },
  {
    id: "sr-5",
    timestamp: "28 Apr 2026, 18:42",
    channel: "adhoc",
    status: "success",
    succeeded: { created: 0, updated: 1, total: 1 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [],
  },
  {
    id: "sr-6",
    timestamp: "28 Apr 2026, 06:00",
    channel: "scheduled",
    status: "success",
    succeeded: { created: 9, updated: 152, total: 162 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [],
  },
  {
    id: "sr-7",
    timestamp: "27 Apr 2026, 11:08",
    channel: "adhoc",
    status: "success",
    succeeded: { created: 0, updated: 1, total: 1 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [],
  },
  {
    id: "sr-8",
    timestamp: "27 Apr 2026, 06:00",
    channel: "scheduled",
    status: "success",
    succeeded: { created: 11, updated: 148, total: 160 },
    failed: { created: 0, updated: 0, total: 0 },
    blocked: 0,
    unblocked: 0,
    failedRecords: [],
  },
];

const LAST_SYNCED_RELATIVE = "27m ago";

// Employee profile mock — keyed by name so any failed-sync or block row can look up
// detail to populate the detail modal.
const EMPLOYEE_PROFILES: Record<string, EmployeeProfile> = {
  "Naledi Khumalo": {
    id: "emp-1001",
    name: "Naledi Khumalo",
    employeeNumber: "ACME-1001",
    site: "Sandton City",
    status: "blocked",
    reason: "Failed sync — banking details (branch code not recognised)",
    blockedSince: "29 Apr 2026, 09:42",
    events: [
      { type: "blocked", description: "Blocked — failed sync on banking update", timestamp: "29 Apr 2026, 09:42" },
      { type: "sync-failed", description: "Update banking details — validation failed", timestamp: "29 Apr 2026, 09:42" },
      { type: "sync-succeeded", description: "Update phone number", timestamp: "26 Apr 2026, 14:10" },
    ],
  },
  "Sipho Dlamini": {
    id: "emp-1002",
    name: "Sipho Dlamini",
    employeeNumber: "ACME-1002",
    site: "Rosebank Mall",
    status: "blocked",
    reason: "Failed sync — tax number (downstream timeout)",
    blockedSince: "29 Apr 2026, 09:18",
    events: [
      { type: "blocked", description: "Blocked — failed sync on tax number update", timestamp: "29 Apr 2026, 09:18" },
      { type: "sync-failed", description: "Update tax number — downstream timeout after 30s", timestamp: "29 Apr 2026, 09:18" },
      { type: "sync-succeeded", description: "Created in integrated system", timestamp: "12 Mar 2026, 11:00" },
    ],
  },
  "Thandi Mokoena": {
    id: "emp-1003",
    name: "Thandi Mokoena",
    employeeNumber: "ACME-1003",
    site: "Carlton Centre",
    status: "blocked",
    reason: "Failed sync — address (postal code missing)",
    blockedSince: "29 Apr 2026, 08:55",
    events: [
      { type: "blocked", description: "Blocked — failed sync on address update", timestamp: "29 Apr 2026, 08:55" },
      { type: "sync-failed", description: "Update address — postal code missing", timestamp: "29 Apr 2026, 08:55" },
    ],
  },
  "Brendan Naidoo": {
    id: "emp-1004",
    name: "Brendan Naidoo",
    employeeNumber: "ACME-1004",
    site: "Gateway Theatre",
    status: "ok",
    events: [
      { type: "sync-failed", description: "Update emergency contact — schema mismatch on phoneNumber", timestamp: "29 Apr 2026, 08:30" },
      { type: "sync-succeeded", description: "Update tax number", timestamp: "20 Apr 2026, 16:22" },
    ],
  },
  "Lerato Mahlangu": {
    id: "emp-1005",
    name: "Lerato Mahlangu",
    employeeNumber: "ACME-1005",
    site: "Baywest Mall",
    status: "ok",
    events: [
      { type: "sync-failed", description: "Leave approval webhook — missing manager ID", timestamp: "29 Apr 2026, 08:12" },
    ],
  },
  "Joseph Pillay": {
    id: "emp-1006",
    name: "Joseph Pillay",
    employeeNumber: "ACME-1006",
    site: "OR Tambo Logistics Hub",
    status: "blocked",
    reason: "Short time — under 40 hrs this week",
    blockedSince: "28 Apr",
    events: [
      { type: "blocked", description: "Blocked — short-time policy triggered", timestamp: "28 Apr 2026, 17:00" },
      { type: "unblocked", description: "Unblocked — week reset", timestamp: "21 Apr 2026, 00:00" },
    ],
  },
  "Mandisa Zulu": {
    id: "emp-1007",
    name: "Mandisa Zulu",
    employeeNumber: "ACME-1007",
    site: "Retail Floor",
    status: "blocked",
    reason: "Company hold — pending HR review",
    blockedSince: "27 Apr",
    events: [
      { type: "blocked", description: "Blocked manually — pending HR review", timestamp: "27 Apr 2026, 11:30" },
    ],
  },
  // Bulk demo profiles — status mix so the failures-by-employee filter has variety
  "Themba Sithole":   { id: "emp-1101", name: "Themba Sithole",   employeeNumber: "ACME-1101", site: "Sandton City",       status: "blocked",   events: [] },
  "Zanele Mthembu":   { id: "emp-1102", name: "Zanele Mthembu",   employeeNumber: "ACME-1102", site: "Rosebank Mall",      status: "blocked",   events: [] },
  "Pieter de Klerk":  { id: "emp-1103", name: "Pieter de Klerk",  employeeNumber: "ACME-1103", site: "Cape Town CBD",      status: "blocked",   events: [] },
  "Khanyi Maseko":    { id: "emp-1104", name: "Khanyi Maseko",    employeeNumber: "ACME-1104", site: "Carlton Centre",     status: "blocked",   events: [] },
  "Tumi Phatudi":     { id: "emp-1105", name: "Tumi Phatudi",     employeeNumber: "ACME-1105", site: "OR Tambo",           status: "blocked",   events: [] },
  "Ahmed Patel":      { id: "emp-1106", name: "Ahmed Patel",      employeeNumber: "ACME-1106", site: "Gateway Theatre",    status: "blocked",   events: [] },
  "Fatima Adams":     { id: "emp-1107", name: "Fatima Adams",     employeeNumber: "ACME-1107", site: "Baywest Mall",       status: "blocked",   events: [] },
  "Mandla Zulu":      { id: "emp-1108", name: "Mandla Zulu",      employeeNumber: "ACME-1108", site: "Sandton City",       status: "blocked",   events: [] },
  "Refiloe Letsoalo": { id: "emp-1109", name: "Refiloe Letsoalo", employeeNumber: "ACME-1109", site: "Rosebank Mall",      status: "unblocked", events: [] },
  "Ryan O'Connor":    { id: "emp-1110", name: "Ryan O'Connor",    employeeNumber: "ACME-1110", site: "Cape Town CBD",      status: "unblocked", events: [] },
  "Nokuthula Tshabalala": { id: "emp-1111", name: "Nokuthula Tshabalala", employeeNumber: "ACME-1111", site: "OR Tambo", status: "unblocked", events: [] },
  "Johan Pretorius":  { id: "emp-1112", name: "Johan Pretorius",  employeeNumber: "ACME-1112", site: "Carlton Centre",     status: "unblocked", events: [] },
  "Zinhle Khumalo":   { id: "emp-1113", name: "Zinhle Khumalo",   employeeNumber: "ACME-1113", site: "Gateway Theatre",    status: "ok",        events: [] },
  "Tshepo Mahlangu":  { id: "emp-1114", name: "Tshepo Mahlangu",  employeeNumber: "ACME-1114", site: "Baywest Mall",       status: "ok",        events: [] },
  "Kwame Asante":     { id: "emp-1115", name: "Kwame Asante",     employeeNumber: "ACME-1115", site: "Sandton City",       status: "ok",        events: [] },
  "Megan Williams":   { id: "emp-1116", name: "Megan Williams",   employeeNumber: "ACME-1116", site: "Rosebank Mall",      status: "ok",        events: [] },
  "Bongiwe Ndlovu":   { id: "emp-1117", name: "Bongiwe Ndlovu",   employeeNumber: "ACME-1117", site: "Cape Town CBD",      status: "blocked",   events: [] },
  "Sibongile Mokoena":{ id: "emp-1118", name: "Sibongile Mokoena",employeeNumber: "ACME-1118", site: "OR Tambo",           status: "blocked",   events: [] },
  "Daniel Stevens":   { id: "emp-1119", name: "Daniel Stevens",   employeeNumber: "ACME-1119", site: "Carlton Centre",     status: "ok",        events: [] },
  "Aisha Ismail":     { id: "emp-1120", name: "Aisha Ismail",     employeeNumber: "ACME-1120", site: "Gateway Theatre",    status: "unblocked", events: [] },
  "Lethabo Sebola":   { id: "emp-1121", name: "Lethabo Sebola",   employeeNumber: "ACME-1121", site: "Baywest Mall",       status: "ok",        events: [] },
  "Carla Botha":      { id: "emp-1122", name: "Carla Botha",      employeeNumber: "ACME-1122", site: "Sandton City",       status: "ok",        events: [] },
  "Lindiwe Khoza":    { id: "emp-1123", name: "Lindiwe Khoza",    employeeNumber: "ACME-1123", site: "Rosebank Mall",      status: "blocked",   events: [] },
  "Anele van der Merwe": { id: "emp-1124", name: "Anele van der Merwe", employeeNumber: "ACME-1124", site: "Cape Town CBD", status: "blocked", events: [] },
};

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const passRate = (m: ChannelMetric): number =>
  m.attempted === 0 ? 100 : Math.round((m.successful / m.attempted) * 100);

const classifyHealth = (rate: number): HealthBucket => {
  if (rate >= 99) return "healthy";
  if (rate >= 90) return "degraded";
  return "down";
};

const HEALTH_LABEL: Record<HealthBucket, string> = {
  healthy: "Healthy",
  degraded: "Degraded",
  down: "Down",
};

const HEALTH_TAG_VARIANT: Record<HealthBucket, "success" | "drafted" | "failed"> = {
  healthy: "success",
  degraded: "drafted",
  down: "failed",
};

const HEALTH_DESCRIPTION: Record<HealthBucket, string> = {
  healthy: "All integration events are succeeding.",
  degraded: "Some events are failing — drill into a channel below to investigate.",
  down: "Most events are failing. Investigate immediately.",
};

/* ================================================================== */
/*  Sub-components                                                     */
/* ================================================================== */

interface DonutProps {
  rate: number;
  bucket: HealthBucket;
  size?: number;
}

function Donut({ rate, bucket, size = 120 }: DonutProps) {
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (rate / 100) * circumference;

  const stops: Record<HealthBucket, string> = {
    healthy: "#22C55E",
    degraded: "#FDBA74",
    down: "#EF4444",
  };

  return (
    <svg width={size} height={size} aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#E5E7EB"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={stops[bucket]}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="font-heading font-semibold fill-greyscale-text-title"
        style={{ fontSize: size / 4 }}
      >
        {rate}%
      </text>
    </svg>
  );
}

interface ChannelCardProps {
  channel: ChannelDetail;
  selected: boolean;
  onSelect: () => void;
}

function ChannelCard({ channel, selected, onSelect }: ChannelCardProps) {
  const rate = passRate(channel.metric);
  const bucket = classifyHealth(rate);
  const failed = channel.metric.attempted - channel.metric.successful;
  const isFailing = failed > 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "text-left rounded-lg border p-md bg-white transition-all w-full",
        "focus:outline-none focus:ring-2 focus:ring-primary-brand-primary/40",
        selected
          ? "border-primary-brand-primary ring-2 ring-primary-brand-primary/20"
          : "border-greyscale-border hover:border-greyscale-text-caption",
      )}
    >
      <div className="flex items-center justify-between mb-xs">
        <div className="flex items-center gap-xs text-greyscale-text-body">
          {channel.icon}
          <span className="text-sm font-semibold text-greyscale-text-title">
            {channel.label}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center text-greyscale-text-caption hover:text-greyscale-text-body cursor-help"
                  aria-label={`About ${channel.label}`}
                >
                  <Info size={14} />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{channel.description}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Tag variant={HEALTH_TAG_VARIANT[bucket]} size="sm">
          {HEALTH_LABEL[bucket]}
        </Tag>
      </div>
      <div className="flex items-baseline gap-xs">
        <span className="font-heading text-xl font-semibold text-greyscale-text-title">
          {channel.metric.successful.toLocaleString()}
        </span>
        <span className="text-sm text-greyscale-text-caption">
          / {channel.metric.attempted.toLocaleString()} ({rate}%)
        </span>
      </div>
      <div className="mt-xs flex items-center justify-between text-xs">
        <span
          className={cn(
            isFailing ? "text-secondary-pink-900 font-semibold" : "text-greyscale-text-caption",
          )}
        >
          {isFailing ? `${failed} failed` : "All passed"}
        </span>
        <span
          className={cn(
            "flex items-center gap-xxs font-semibold",
            selected ? "text-primary-brand-primary" : "text-greyscale-text-caption",
          )}
        >
          {selected ? "Viewing details" : "View details"}
          <ArrowRight size={12} />
        </span>
      </div>
    </button>
  );
}

interface ChannelDetailPanelProps {
  channel: ChannelDetail;
  onEmployeeClick: (name: string) => void;
}

function ChannelDetailPanel({ channel, onEmployeeClick }: ChannelDetailPanelProps) {
  const breakdownTotal = channel.breakdown.reduce((sum, b) => sum + b.attempted, 0);

  const failedColumns: ColumnDef<FailedRecord>[] = [
    {
      accessorKey: "timestamp",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Time" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-caption">{row.original.timestamp}</span>
      ),
    },
    {
      accessorKey: "employeeName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.employeeName}
        </span>
      ),
    },
    {
      id: "employeeId",
      accessorFn: (row) => {
        const profile = EMPLOYEE_PROFILES[row.employeeName];
        return profile?.employeeNumber ?? fallbackEmployeeId(row.employeeName);
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee ID" />,
      cell: ({ row }) => {
        const profile = EMPLOYEE_PROFILES[row.original.employeeName];
        return (
          <span className="text-sm font-mono text-greyscale-text-caption">
            {profile?.employeeNumber ?? fallbackEmployeeId(row.original.employeeName)}
          </span>
        );
      },
    },
    {
      id: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const profile = EMPLOYEE_PROFILES[row.original.employeeName];
        const status = profile?.status ?? "ok";
        return (
          <Tag variant={STATUS_TAG[status].variant} size="sm">
            {STATUS_TAG[status].label}
          </Tag>
        );
      },
    },
    {
      accessorKey: "detail",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Detail" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{row.original.detail}</span>
      ),
    },
    {
      accessorKey: "reason",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Reason" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{row.original.reason}</span>
      ),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEmployeeClick(row.original.employeeName)}
            aria-label={`View history for ${row.original.employeeName}`}
          >
            <Eye size={14} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md">
      <div className="flex flex-wrap items-center justify-between gap-sm mb-sm">
        <div className="flex items-center gap-xs">
          {channel.icon}
          <h3 className="text-sm font-semibold text-greyscale-text-title">
            {channel.label} · {channel.breakdownLabel}
          </h3>
        </div>
        {/* Per-channel block impact — what this channel did to people */}
        <div className="flex items-center gap-md text-xs text-greyscale-text-body">
          <span className="flex items-center gap-xxs">
            <UserMinus size={12} className="text-secondary-pink-900" />
            <span className="font-semibold text-greyscale-text-title">
              {channel.newBlocks}
            </span>
            <span className="text-greyscale-text-caption">new blocks</span>
          </span>
          <span className="flex items-center gap-xxs">
            <UserPlus size={12} className="text-greyscale-text-caption" />
            <span className="font-semibold text-greyscale-text-title">
              {channel.unblocked}
            </span>
            <span className="text-greyscale-text-caption">unblocked</span>
          </span>
        </div>
      </div>

      {/* Breakdown bars — proportional, not equal-weight tiles */}
      <div className="space-y-sm">
        {channel.breakdown.map((b) => {
          const failed = b.attempted - b.successful;
          const widthPct = breakdownTotal === 0 ? 0 : (b.attempted / breakdownTotal) * 100;
          const passPct = b.attempted === 0 ? 0 : (b.successful / b.attempted) * 100;
          const isFailing = failed > 0;
          return (
            <div key={b.key}>
              <div className="flex items-baseline justify-between mb-xxs">
                <span className="text-sm text-greyscale-text-body">{b.label}</span>
                <span
                  className={cn(
                    "text-sm",
                    isFailing
                      ? "text-secondary-pink-900 font-semibold"
                      : "text-greyscale-text-caption",
                  )}
                >
                  {b.successful} / {b.attempted}
                  {isFailing && ` · ${failed} failed`}
                </span>
              </div>
              <div
                className="h-[6px] rounded-full bg-neutral-100 overflow-hidden"
                style={{ width: `${Math.max(widthPct, 8)}%` }}
              >
                <div
                  className={cn(
                    "h-full",
                    isFailing ? "bg-secondary-pink-300" : "bg-primary-brand-primary",
                  )}
                  style={{ width: `${passPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {channel.failedRecords.length > 0 && (
        <>
          <Divider className="my-md" />
          <DataTable
            columns={failedColumns}
            data={channel.failedRecords}
            toolbarChildren={
              <div className="flex items-center gap-xs">
                <AlertTriangle size={14} className="text-secondary-pink-900" />
                <h4 className="text-sm font-semibold text-greyscale-text-title">
                  Failures requiring attention ({channel.failedRecords.length})
                </h4>
              </div>
            }
          />
        </>
      )}
    </div>
  );
}

interface EmployeeDetailDialogProps {
  employeeName: string | null;
  onClose: () => void;
}

// Deterministic fallback ID for any employee not in EMPLOYEE_PROFILES — keeps
// the Employee ID column populated for prototype demo data.
const fallbackEmployeeId = (name: string): string => {
  const sum = Array.from(name).reduce((s, c) => s + c.charCodeAt(0), 0);
  const num = 1000 + (sum % 9000);
  return `ACME-${num}`;
};

// Reference "now" for period filtering — pinned so the prototype's mock
// timestamps line up consistently regardless of when the user opens it.
const NOW_REFERENCE = new Date(2026, 3, 30, 14, 30); // 30 Apr 2026, 14:30

const MONTH_INDEX: Record<string, number> = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

// Parses timestamps in the prototype format "30 Apr 2026, 14:02".
const parseTimestamp = (ts: string): Date | null => {
  const match = ts.match(/^(\d+) (\w+) (\d+), (\d+):(\d+)$/);
  if (!match) return null;
  const [, day, month, year, hour, minute] = match;
  const m = MONTH_INDEX[month];
  if (m === undefined) return null;
  return new Date(Number(year), m, Number(day), Number(hour), Number(minute));
};

const isWithinPeriod = (ts: string, period: Period): boolean => {
  const date = parseTimestamp(ts);
  if (!date) return true;
  if (period === "today") {
    return (
      date.getFullYear() === NOW_REFERENCE.getFullYear() &&
      date.getMonth() === NOW_REFERENCE.getMonth() &&
      date.getDate() === NOW_REFERENCE.getDate()
    );
  }
  if (period === "7d") {
    const cutoff = new Date(NOW_REFERENCE);
    cutoff.setDate(cutoff.getDate() - 7);
    return date >= cutoff;
  }
  if (period === "30d") {
    const cutoff = new Date(NOW_REFERENCE);
    cutoff.setDate(cutoff.getDate() - 30);
    return date >= cutoff;
  }
  // "custom" — no client-side range picker yet; show everything.
  return true;
};

const STATUS_TAG: Record<EmployeeProfile["status"], { variant: "success" | "drafted" | "failed"; label: string }> = {
  ok: { variant: "success", label: "Unblocked" },
  blocked: { variant: "failed", label: "Blocked" },
  unblocked: { variant: "drafted", label: "Recently unblocked" },
};

const EVENT_DOT: Record<EmployeeEvent["type"], string> = {
  blocked: "bg-secondary-pink-300",
  unblocked: "bg-greyscale-text-caption",
  "sync-failed": "bg-secondary-pink-300",
  "sync-succeeded": "bg-primary-brand-primary",
};

function EmployeeDetailDialog({ employeeName, onClose }: EmployeeDetailDialogProps) {
  const profile = employeeName ? EMPLOYEE_PROFILES[employeeName] : null;
  const open = profile !== null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[95vw] max-h-[90vh] flex flex-col">
        {profile && (
          <div className="flex flex-col gap-md flex-1 min-h-0">
            <DialogHeader>
              <div className="flex items-center gap-xs">
                <DialogTitle>{profile.name}</DialogTitle>
                <Tag variant={STATUS_TAG[profile.status].variant} size="sm">
                  {STATUS_TAG[profile.status].label}
                </Tag>
              </div>
              <DialogDescription>
                {profile.employeeNumber} · {profile.site}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-md flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
            {profile.reason && (
              <div className="rounded-md bg-secondary-pink-50 border border-secondary-pink-300 p-sm">
                <div className="text-xs font-semibold uppercase tracking-wide text-secondary-pink-900 mb-xxs">
                  Current block reason
                </div>
                <div className="text-sm text-greyscale-text-title">{profile.reason}</div>
                {profile.blockedSince && (
                  <div className="text-xs text-greyscale-text-caption mt-xxs">
                    Blocked since {profile.blockedSince}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption mb-sm">
                Recent integration events
              </div>
              <ol className="space-y-sm">
                {profile.events.map((ev, idx) => (
                  <li key={idx} className="flex gap-sm">
                    <div className="flex flex-col items-center">
                      <span className={cn("w-2 h-2 rounded-full mt-[6px]", EVENT_DOT[ev.type])} />
                      {idx < profile.events.length - 1 && (
                        <span className="w-px flex-1 bg-greyscale-border my-xxs" />
                      )}
                    </div>
                    <div className="pb-sm">
                      <div className="text-sm text-greyscale-text-title">{ev.description}</div>
                      <div className="text-xs text-greyscale-text-caption">{ev.timestamp}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            </div>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
              {profile.status === "blocked" && (
                <Button size="sm">
                  <RefreshCw size={14} />
                  Retry sync
                </Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface SyncRunDetailDialogProps {
  run: SyncRun | null;
  onClose: () => void;
}

function SyncRunDetailDialog({ run, onClose }: SyncRunDetailDialogProps) {
  const [failureStatusFilter, setFailureStatusFilter] = useState<EmployeeProfile["status"] | "all">("all");

  const failureColumns: ColumnDef<SyncRunFailure>[] = [
    {
      accessorKey: "employeeName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.employeeName}
        </span>
      ),
    },
    {
      id: "employeeId",
      accessorFn: (row) => {
        const profile = EMPLOYEE_PROFILES[row.employeeName];
        return profile?.employeeNumber ?? fallbackEmployeeId(row.employeeName);
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee ID" />,
      cell: ({ row }) => {
        const profile = EMPLOYEE_PROFILES[row.original.employeeName];
        return (
          <span className="text-sm font-mono text-greyscale-text-caption">
            {profile?.employeeNumber ?? fallbackEmployeeId(row.original.employeeName)}
          </span>
        );
      },
    },
    {
      id: "status",
      accessorFn: (row) => {
        const profile = EMPLOYEE_PROFILES[row.employeeName];
        return profile?.status ?? "ok";
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const profile = EMPLOYEE_PROFILES[row.original.employeeName];
        const empStatus = profile?.status ?? "ok";
        return (
          <Tag variant={STATUS_TAG[empStatus].variant} size="sm">
            {STATUS_TAG[empStatus].label}
          </Tag>
        );
      },
    },
    {
      accessorKey: "detail",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Detail" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{row.original.detail}</span>
      ),
    },
    {
      accessorKey: "reason",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Reason" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{row.original.reason}</span>
      ),
    },
  ];

  const open = run !== null;
  const totalAttempted = run ? run.succeeded.total + run.failed.total : 0;
  const passRate = run && totalAttempted > 0 ? Math.round((run.succeeded.total / totalAttempted) * 100) : 0;
  const channelLabel = run?.channel === "scheduled" ? "Scheduled" : "Adhoc";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[95vw] max-h-[90vh] flex flex-col">
        {run && (
          <div className="flex flex-col gap-md flex-1 min-h-0">
            <DialogHeader className="text-center">
              <div className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
                {run.timestamp}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-xs">
                <DialogTitle>Sync run</DialogTitle>
                <span className="text-sm text-greyscale-text-body">{channelLabel}</span>
                <Tag variant={SYNC_STATUS_TAG[run.status].variant} size="sm">
                  {SYNC_STATUS_TAG[run.status].label}
                </Tag>
              </div>
            </DialogHeader>

            <div className="flex flex-col gap-md flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
            {/* Breakdown stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-md rounded-md bg-neutral-50 p-md">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                  Pass rate
                </div>
                <div className="font-heading text-base font-semibold text-greyscale-text-title">
                  {passRate}%
                </div>
                <div className="text-xs text-greyscale-text-caption">
                  {run.succeeded.total} of {totalAttempted}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                  Created
                </div>
                <div className="flex items-baseline gap-xs">
                  <span className="font-heading text-base font-semibold text-greyscale-text-title">
                    {run.succeeded.created}
                  </span>
                  {run.failed.created > 0 && (
                    <span className="text-xs font-semibold text-secondary-pink-900">
                      {run.failed.created} failed
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                  Updated
                </div>
                <div className="flex items-baseline gap-xs">
                  <span className="font-heading text-base font-semibold text-greyscale-text-title">
                    {run.succeeded.updated}
                  </span>
                  {run.failed.updated > 0 && (
                    <span className="text-xs font-semibold text-secondary-pink-900">
                      {run.failed.updated} failed
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                  Blocked
                </div>
                <div className="font-heading text-base font-semibold text-greyscale-text-title">
                  +{run.blocked}
                </div>
                <div className="text-xs text-greyscale-text-caption">new this run</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-greyscale-text-caption">
                  Unblocked
                </div>
                <div className="font-heading text-base font-semibold text-greyscale-text-title">
                  {run.unblocked}
                </div>
                <div className="text-xs text-greyscale-text-caption">by this run</div>
              </div>
            </div>

            {/* Failures table */}
            {run.failedRecords.length > 0 ? (
              <div>
                <h4 className="text-sm font-semibold text-greyscale-text-title">
                  Failures in this sync ({run.failedRecords.length})
                </h4>
                <DataTable
                  columns={failureColumns}
                  data={run.failedRecords.filter((rec) => {
                    if (failureStatusFilter === "all") return true;
                    const profile = EMPLOYEE_PROFILES[rec.employeeName];
                    const status = profile?.status ?? "ok";
                    return status === failureStatusFilter;
                  })}
                  showPagination
                  filterColumn="employeeName"
                  filterPlaceholder="Search employee"
                  toolbarChildren={
                    <Select
                      value={failureStatusFilter}
                      onValueChange={(v) =>
                        setFailureStatusFilter(v as EmployeeProfile["status"] | "all")
                      }
                    >
                      <SelectTrigger className="h-8 w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="blocked">Blocked</SelectItem>
                        <SelectItem value="ok">Unblocked</SelectItem>
                        <SelectItem value="unblocked">Recently unblocked</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                />
              </div>
            ) : (
              <div className="rounded-md border border-greyscale-border bg-white p-md text-sm text-greyscale-text-caption text-center">
                No failures in this sync — all events succeeded.
              </div>
            )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EmployeeFailureRow {
  syncId: string;
  syncTimestamp: string;
  syncRun: SyncRun;
  employeeName: string;
  detail: string;
  reason: string;
}

function EmployeeFailuresTable({
  failures,
  onSyncClick,
}: {
  failures: EmployeeFailureRow[];
  onSyncClick: (run: SyncRun) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<EmployeeProfile["status"] | "all">("all");

  const columns: ColumnDef<EmployeeFailureRow>[] = [
    {
      accessorKey: "employeeName",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.employeeName}
        </span>
      ),
    },
    {
      id: "employeeId",
      accessorFn: (row) => {
        const profile = EMPLOYEE_PROFILES[row.employeeName];
        return profile?.employeeNumber ?? fallbackEmployeeId(row.employeeName);
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee ID" />,
      cell: ({ row }) => {
        const profile = EMPLOYEE_PROFILES[row.original.employeeName];
        return (
          <span className="text-sm font-mono text-greyscale-text-caption">
            {profile?.employeeNumber ?? fallbackEmployeeId(row.original.employeeName)}
          </span>
        );
      },
    },
    {
      id: "status",
      accessorFn: (row) => {
        const profile = EMPLOYEE_PROFILES[row.employeeName];
        return profile?.status ?? "ok";
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const profile = EMPLOYEE_PROFILES[row.original.employeeName];
        const empStatus = profile?.status ?? "ok";
        return (
          <Tag variant={STATUS_TAG[empStatus].variant} size="sm">
            {STATUS_TAG[empStatus].label}
          </Tag>
        );
      },
    },
    {
      accessorKey: "detail",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Detail" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{row.original.detail}</span>
      ),
    },
    {
      accessorKey: "reason",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Reason" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{row.original.reason}</span>
      ),
    },
    {
      id: "syncTimestamp",
      accessorKey: "syncTimestamp",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Sync" />,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => onSyncClick(row.original.syncRun)}
          className="text-sm text-primary-brand-primary underline-offset-2 hover:underline focus:outline-none focus:underline"
        >
          {row.original.syncTimestamp}
        </button>
      ),
    },
  ];

  const filtered = failures.filter((f) => {
    if (statusFilter === "all") return true;
    const profile = EMPLOYEE_PROFILES[f.employeeName];
    const status = profile?.status ?? "ok";
    return status === statusFilter;
  });

  return (
    <DataTable
      columns={columns}
      data={filtered}
      showPagination
      filterColumn="employeeName"
      filterPlaceholder="Search employee"
      toolbarChildren={
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as EmployeeProfile["status"] | "all")}
        >
          <SelectTrigger className="h-8 w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
            <SelectItem value="ok">Unblocked</SelectItem>
            <SelectItem value="unblocked">Recently unblocked</SelectItem>
          </SelectContent>
        </Select>
      }
    />
  );
}

function SyncRunsTable({ runs, onRunClick }: { runs: SyncRun[]; onRunClick: (run: SyncRun) => void }) {
  const syncColumns: ColumnDef<SyncRun>[] = [
    {
      accessorKey: "timestamp",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Time" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">{row.original.timestamp}</span>
      ),
    },
    {
      id: "created",
      accessorFn: (row) => row.succeeded.created,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.succeeded.created.toLocaleString()}
        </span>
      ),
    },
    {
      id: "updated",
      accessorFn: (row) => row.succeeded.updated,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Updated" />,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.succeeded.updated.toLocaleString()}
        </span>
      ),
    },
    {
      accessorKey: "blocked",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Blocked" />,
      cell: ({ row }) => (
        <span
          className={cn(
            "text-sm font-semibold",
            row.original.blocked > 0 ? "text-secondary-pink-900" : "text-greyscale-text-caption",
          )}
        >
          {row.original.blocked}
        </span>
      ),
    },
    {
      accessorKey: "unblocked",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Unblocked" />,
      cell: ({ row }) => (
        <span className="text-sm font-semibold text-greyscale-text-title">
          {row.original.unblocked}
        </span>
      ),
    },
    {
      accessorKey: "channel",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Channel" />,
      cell: ({ row }) => (
        <span className="text-sm text-greyscale-text-body">
          {row.original.channel === "scheduled" ? "Scheduled" : "Adhoc"}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const meta = SYNC_STATUS_TAG[row.original.status];
        return (
          <Tag variant={meta.variant} size="sm">
            {meta.label}
          </Tag>
        );
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRunClick(row.original)}
            aria-label={`View details for ${row.original.timestamp}`}
          >
            <Eye size={14} />
          </Button>
        </div>
      ),
    },
  ];

  return <DataTable columns={syncColumns} data={runs} showPagination showToolbar={false} />;
}


interface ClientPickerProps {
  value: string;
  onChange: (value: string) => void;
  options: ClientOption[];
}

function ClientPicker({ value, onChange, options }: ClientPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  if (options.length <= 1) {
    return (
      <div className="flex items-center gap-xs h-[46px] px-4 text-sm font-semibold text-greyscale-text-body">
        <Users size={16} className="text-greyscale-text-caption" />
        <span>{selected?.label}</span>
      </div>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between gap-2 min-w-[200px] h-[46px] rounded-lg border border-greyscale-border bg-white px-4 py-3 text-sm font-semibold text-greyscale-text-body transition-all outline-none hover:border-greyscale-text-caption focus:border-greyscale-text-caption data-[state=open]:border-greyscale-text-caption"
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="flex items-center gap-xs truncate">
            <Users size={16} className="text-greyscale-text-caption shrink-0" />
            <span className="truncate">{selected?.label}</span>
          </span>
          <ChevronDown size={16} className="text-greyscale-text-caption shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="p-xs border-b border-greyscale-border">
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search clients"
          />
        </div>
        <ul role="listbox" className="max-h-[240px] overflow-auto py-xxs">
          {filtered.length === 0 ? (
            <li className="px-sm py-xs text-sm text-greyscale-text-caption">No matches.</li>
          ) : (
            filtered.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between w-full px-sm py-xs text-sm hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none"
                >
                  <span className="text-greyscale-text-title">{opt.label}</span>
                  {opt.value === value && (
                    <Check size={16} className="text-primary-brand-primary" />
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

// Optional URL escape hatch for previewing non-default states without exposing
// a switcher on the dashboard. Add ?state=loading | empty | error to the URL.
const initialDataState = (): DataState => {
  if (typeof window === "undefined") return "ready";
  const param = new URLSearchParams(window.location.search).get("state");
  if (param === "loading" || param === "empty" || param === "error") return param;
  return "ready";
};

export function IntegrationHealth() {
  const [period, setPeriod] = useState<Period>("today");
  const [statusFilter, setStatusFilter] = useState<SyncRunStatus | "all">("all");
  const [dataState, setDataState] = useState<DataState>(initialDataState);
  const [viewMode, setViewMode] = useState<"by-sync" | "by-employee">("by-sync");

  const filteredRuns = useMemo(
    () =>
      SYNC_RUNS.filter(
        (r) =>
          (statusFilter === "all" || r.status === statusFilter) &&
          isWithinPeriod(r.timestamp, period),
      ),
    [statusFilter, period],
  );

  const allFailures: EmployeeFailureRow[] = useMemo(
    () =>
      filteredRuns.flatMap((run) =>
        run.failedRecords.map((rec) => ({
          syncId: run.id,
          syncTimestamp: run.timestamp,
          syncRun: run,
          employeeName: rec.employeeName,
          detail: rec.detail,
          reason: rec.reason,
        })),
      ),
    [filteredRuns],
  );
  const [selectedChannel, setSelectedChannel] = useState<ChannelKey>("scheduled-sync");
  const [employeeInView, setEmployeeInView] = useState<string | null>(null);
  const [runInView, setRunInView] = useState<SyncRun | null>(null);

  const overallRate = passRate(OVERALL);
  const overallBucket = classifyHealth(overallRate);
  const overallFailed = OVERALL.attempted - OVERALL.successful;

  const channelInView = CHANNELS.find((c) => c.key === selectedChannel)!;

  return (
    <div className="pb-xl pt-md">
      <div className="mb-md">
        <h2 className="font-heading text-xl font-semibold leading-tight text-greyscale-text-title">
          Recent syncs
        </h2>
        <p className="text-sm text-greyscale-text-body mt-xxs">
          {OVERALL_SUCCEEDED_CREATED} created
          {OVERALL_FAILED_CREATED > 0 && (
            <span className="text-secondary-pink-900 font-semibold"> ({OVERALL_FAILED_CREATED} failed)</span>
          )}
          {" · "}
          {OVERALL_SUCCEEDED_UPDATED} updated
          {OVERALL_FAILED_UPDATED > 0 && (
            <span className="text-secondary-pink-900 font-semibold"> ({OVERALL_FAILED_UPDATED} failed)</span>
          )}
        </p>
      </div>

      <Tabs
        value={viewMode}
        onValueChange={(v) => setViewMode(v as "by-sync" | "by-employee")}
      >
        <TabsList className="mb-md">
          <TabsTrigger value="by-sync">By sync</TabsTrigger>
          <TabsTrigger value="by-employee">By employee</TabsTrigger>
        </TabsList>

      {/* Control row */}
      <div className="flex flex-wrap items-end gap-md justify-between">
        <div className="flex flex-wrap items-end gap-sm">
          <div>
            <Label htmlFor="period-select" className="text-xs text-greyscale-text-caption">
              Period
            </Label>
            <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <SelectTrigger id="period-select" className="min-w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="custom">Custom range…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="status-select" className="text-xs text-greyscale-text-caption">
              Status
            </Label>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as SyncRunStatus | "all")}
            >
              <SelectTrigger id="status-select" className="min-w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="in-progress">In progress</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="partial-success">Partial success</SelectItem>
                <SelectItem value="failure">Failure</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-sm">
          <span className="flex items-center gap-xxs text-xs text-greyscale-text-caption">
            <Clock size={14} />
            Last synced {LAST_SYNCED_RELATIVE}
          </span>
          <Button variant="outline" size="sm">
            <RefreshCw size={14} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Data state branches */}
      {dataState === "loading" && (
        <div className="mt-lg space-y-md">
          <Skeleton className="h-[200px] w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
            <Skeleton className="h-[120px] w-full" />
            <Skeleton className="h-[120px] w-full" />
          </div>
          <Skeleton className="h-[300px] w-full" />
        </div>
      )}

      {dataState === "error" && (
        <Alert variant="destructive" className="mt-lg">
          <AlertTitle>We couldn&apos;t load integration health</AlertTitle>
          <AlertDescription>
            The metrics service is unreachable. Try refreshing in a minute.
            <div className="mt-sm">
              <Button variant="outline" size="sm" onClick={() => setDataState("ready")}>
                <RefreshCw size={14} />
                Try again
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {dataState === "empty" && (
        <div className="mt-lg">
          <EmptyState
            icon={<Activity size={32} />}
            title="No integration activity in this window"
            description="Pick a longer period or check that an integration is configured for this client."
          />
        </div>
      )}

      {dataState === "ready" && (
        <>
          <TabsContent value="by-sync" className="mt-sm">
            <SyncRunsTable runs={filteredRuns} onRunClick={setRunInView} />
          </TabsContent>
          <TabsContent value="by-employee" className="mt-sm">
            <EmployeeFailuresTable failures={allFailures} onSyncClick={setRunInView} />
          </TabsContent>
        </>
      )}
      </Tabs>

      <EmployeeDetailDialog
        employeeName={employeeInView}
        onClose={() => setEmployeeInView(null)}
      />

      <SyncRunDetailDialog
        run={runInView}
        onClose={() => setRunInView(null)}
      />
    </div>
  );
}
